import styled from "styled-components";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Instagram,
  Linkedin,
  Sparkles,
  Youtube,
} from "lucide-react";

const FounderImg = "/founder.png";

const Section = styled.section`
  position: relative;
  padding: 132px 20px;
  background:
    radial-gradient(900px at 16% 0%, rgba(245, 158, 11, 0.1), transparent 55%),
    radial-gradient(760px at 84% 24%, rgba(56, 189, 248, 0.12), transparent 55%),
    ${({ theme }) => theme.colors.bg};
  overflow: hidden;
`;

const AmbientOrb = styled.div`
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
  filter: blur(10px);
  opacity: 0.6;
`;

const Card = styled(motion.div)`
  max-width: 1020px;
  margin: auto;
  padding: 28px;
  border-radius: 28px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.94), rgba(2, 6, 23, 0.92)),
    linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(56, 189, 248, 0.08));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 44px 120px rgba(2, 6, 23, 0.58);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(520px at 0% 0%, rgba(56, 189, 248, 0.12), transparent 60%),
      radial-gradient(480px at 100% 100%, rgba(245, 158, 11, 0.1), transparent 60%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 18px;
    border-radius: 22px;
  }
`;

const Grid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 22px;
  align-items: stretch;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const ProfilePanel = styled(motion.div)`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.58);
  border: 1px solid rgba(148, 163, 184, 0.14);
  display: flex;
  flex-direction: column;
`;

const AvatarShell = styled.div`
  position: relative;
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.16),
    rgba(56, 189, 248, 0.14)
  );
  border: 1px solid rgba(148, 163, 184, 0.16);
  aspect-ratio: 1 / 1;
`;

const Avatar = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PresencePill = styled.div`
  position: absolute;
  left: 16px;
  bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 800;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.16);
`;

const Name = styled.div`
  margin-top: 14px;
  color: #f8fafc;
  font-size: 22px;
  font-weight: 1000;
`;

const Role = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.6;
`;

const MiniStats = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

const MiniCard = styled.div`
  padding: 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const MiniValue = styled.div`
  color: #f8fafc;
  font-size: 16px;
  font-weight: 900;
`;

const MiniLabel = styled.div`
  margin-top: 5px;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.5;
`;

const Links = styled.div`
  margin-top: 14px;
  display: grid;
  gap: 8px;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 11px 13px;
  border-radius: 14px;
  text-decoration: none;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 800;
  background: rgba(15, 23, 42, 0.52);
  border: 1px solid rgba(148, 163, 184, 0.12);
  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(56, 189, 248, 0.24);
    color: #f8fafc;
  }
`;

const ContentPanel = styled(motion.div)`
  padding: 22px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.14);

  @media (max-width: 768px) {
    padding: 18px 16px;
    border-radius: 22px;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin-top: 14px;
  font-size: clamp(30px, 3.6vw, 46px);
  line-height: 1.02;
  letter-spacing: -0.04em;
  font-weight: 1000;
  color: #f8fafc;
`;

const Lead = styled.p`
  margin-top: 14px;
  max-width: 700px;
  color: #cbd5e1;
  font-size: 16px;
  line-height: 1.75;
`;

const QuoteCard = styled(motion.div)`
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.12),
    rgba(56, 189, 248, 0.08)
  );
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const Quote = styled.div`
  color: #f8fafc;
  font-size: 16px;
  line-height: 1.65;
  font-weight: 700;
`;

const StoryGrid = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const StoryCard = styled(motion.div)`
  padding: 16px;
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const StoryTitle = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 900;
`;

const StoryText = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.65;
`;

const Principles = styled.div`
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Principle = styled(motion.div)`
  padding: 9px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.58);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 800;
`;

const fadeUp = {
  hidden: { opacity: 0, y: 42 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.06,
    },
  },
};

export default function FounderCardSection() {
  return (
    <Section>
      <AmbientOrb
        style={{
          top: "10%",
          right: "-8%",
          width: 280,
          height: 280,
          background:
            "radial-gradient(circle, rgba(56,189,248,0.14) 0%, rgba(56,189,248,0) 72%)",
        }}
      />
      <AmbientOrb
        style={{
          bottom: "8%",
          left: "-6%",
          width: 260,
          height: 260,
          background:
            "radial-gradient(circle, rgba(245,158,11,0.14) 0%, rgba(245,158,11,0) 72%)",
        }}
      />

      <Card
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
      >
        <Grid>
          <ProfilePanel
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <AvatarShell>
              <Avatar
                src={FounderImg}
                alt="Gaurav Bhadauria - Founder of skilll.dev"
                initial={{ scale: 1.06, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              />
              <PresencePill>
                <Dot />
                Building in public
              </PresencePill>
            </AvatarShell>

            <Name>Gaurav Bhadauria</Name>
            <Role>Software Engineer, mentor, and creator of Engineer Codewala.</Role>

            <MiniStats>
              <MiniCard>
                <MiniValue>Clarity first</MiniValue>
                <MiniLabel>Built to reduce confusion and make progress easier to follow.</MiniLabel>
              </MiniCard>
              <MiniCard>
                <MiniValue>Execution focus</MiniValue>
                <MiniLabel>Made for people who want to finish, not just keep consuming.</MiniLabel>
              </MiniCard>
            </MiniStats>

            <Links>
              <SocialLink
                href="https://www.linkedin.com/"
                target="_blank"
                rel="noreferrer"
              >
                <span>
                  <Linkedin size={16} style={{ marginRight: 8 }} />
                  LinkedIn
                </span>
                <ArrowUpRight size={16} />
              </SocialLink>

              <SocialLink
                href="https://www.youtube.com/"
                target="_blank"
                rel="noreferrer"
              >
                <span>
                  <Youtube size={16} style={{ marginRight: 8 }} />
                  YouTube
                </span>
                <ArrowUpRight size={16} />
              </SocialLink>

              <SocialLink
                href="https://www.instagram.com/"
                target="_blank"
                rel="noreferrer"
              >
                <span>
                  <Instagram size={16} style={{ marginRight: 8 }} />
                  Instagram
                </span>
                <ArrowUpRight size={16} />
              </SocialLink>
            </Links>
          </ProfilePanel>

          <ContentPanel
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
          >
            <Eyebrow>
              <Sparkles size={14} />
              Built from real learner struggles
            </Eyebrow>

            <Heading>Why skilll.dev was built</Heading>

            <Lead>
              I kept seeing the same problem: serious learners were putting in
              effort, but still lacked a clear path and a daily system they
              could actually follow.
            </Lead>

            <QuoteCard
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.14, duration: 0.6 }}
            >
              <Quote>
                “The issue was rarely effort. It was direction, sequencing, and
                knowing what to do next.”
              </Quote>
            </QuoteCard>

            <StoryGrid as={motion.div} variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <StoryCard variants={fadeUp} whileHover={{ y: -4 }}>
                <StoryTitle>The pattern</StoryTitle>
                <StoryText>
                  Too many technologies, too many opinions, and no clear next
                  step people could trust day by day.
                </StoryText>
              </StoryCard>

              <StoryCard variants={fadeUp} whileHover={{ y: -4 }}>
                <StoryTitle>The response</StoryTitle>
                <StoryText>
                  skilll.dev turns goals into a roadmap with phases, weeks, and
                  days so progress feels clear and practical.
                </StoryText>
              </StoryCard>
            </StoryGrid>

            <Principles as={motion.div} variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}>
              {[
                "Clarity over hype",
                "Daily execution",
                "Projects over passive theory",
              ].map((item) => (
                <Principle key={item} variants={fadeUp} whileHover={{ y: -2 }}>
                  {item}
                </Principle>
              ))}
            </Principles>
          </ContentPanel>
        </Grid>
      </Card>
    </Section>
  );
}
